"use client";

import Link from "next/link";
import Image from "next/image";
import { useMemo } from "react";
import { useLocale } from "@/lib/locale/locale-provider";
import { getWeddingGalleryPath } from "@/lib/wedding-routes";
import type { GallerySectionProps } from "@/lib/sections/gallery.type";

export function GallerySection({ title, images = [], albums = [] }: GallerySectionProps) {
  const locale = useLocale();
  const copy =
    locale === "fr"
      ? {
          empty: "Aucune photo n'est encore disponible.",
          title: "Encore plus de photos et de souvenirs",
          hint: "Choisissez un chapitre pour ouvrir la galerie complète.",
          fallbackDescription: "Ouvrez cette section pour voir plus de photos.",
        }
      : locale === "en"
        ? {
            empty: "No gallery images yet.",
            title: "More photos and memories",
            hint: "Choose a chapter to open the full gallery.",
            fallbackDescription: "Open this section to view more photos.",
          }
        : {
            empty: "Noch keine Galeriebilder vorhanden.",
            title: "Mehr Fotos und Erinnerungen",
            hint: "Waehlt ein Kapitel, um die komplette Galerie zu oeffnen.",
            fallbackDescription: "Oeffnet diesen Bereich, um mehr Fotos zu sehen.",
          };

  const normalizedAlbums = useMemo(() => {
    if (albums.length > 0) {
      return albums.filter((album) => album.images.length > 0);
    }

    if (images.length > 0) {
        return [
          {
            id: "default-gallery",
            title: copy.title,
            place: "",
            description: "",
            images,
        },
      ];
    }

    return [];
  }, [albums, copy.title, images]);

  if (normalizedAlbums.length === 0) {
    return (
      <div className="bg-white py-24">
        <div className="mx-auto max-w-6xl px-4 text-center">
          {title ? <h2 className="font-serif text-3xl md:text-4xl">{title}</h2> : null}
          <p className="mt-4 text-gray-500">{copy.empty}</p>
        </div>
      </div>
    );
  }

  const getCardHeightClass = (index: number) => {
    const pattern = index % 3;
    if (pattern === 0) {
      return "h-[30rem] md:h-[36rem]";
    }
    if (pattern === 1) {
      return "h-[26rem] md:h-[32rem]";
    }
    return "h-[24rem] md:h-[30rem]";
  };

  return (
    <div className="bg-[#f6f6f6] py-24 md:py-28">
      <div className="mx-auto max-w-6xl space-y-12 px-4 md:space-y-14">
        <header className="mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-3xl text-gray-900 md:text-4xl">
            {title ?? copy.title}
          </h2>
          <p className="mt-4 text-sm text-amber-700 md:text-base">
            {copy.hint}
          </p>
        </header>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
          {normalizedAlbums.map((album, index) => {
            const cover = album.images[0];

            return (
              <Link
                key={album.id}
                href={getWeddingGalleryPath(locale, album.id)}
                className={`group relative block overflow-hidden rounded-3xl border border-gray-200 text-left transition hover:border-amber-300 ${getCardHeightClass(
                  index,
                )}`}
              >
                <Image
                  src={cover.src}
                  alt={cover.alt ?? album.title}
                  fill
                  sizes="(max-width: 767px) 100vw, 50vw"
                  className="object-cover transition duration-500 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />

                <div className="absolute inset-x-5 bottom-5 space-y-3 md:inset-x-6 md:bottom-6 md:space-y-4">
                  <h3 className="font-serif text-3xl text-white md:text-4xl">{album.title}</h3>
                  {album.place ? (
                    <p className="text-sm uppercase tracking-[0.14em] text-amber-200">{album.place}</p>
                  ) : null}
                  <div className="h-px w-20 bg-white/60" />
                  <p className="line-clamp-3 max-w-xl text-sm leading-relaxed text-white/85">
                    {album.description ?? copy.fallbackDescription}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
